import { Link } from "react-router-dom";
import { ShieldCheck, Radar, FileCheck2, Radio, ArrowRight, Phone, Mail, Shield } from "lucide-react";
import { services } from "../data/services";
import { sectors } from "../data/sectors";
import { provinces } from "../data/provinces";
import { cities } from "../data/cities";
import { insights } from "../data/insights";
import { getHead, PHONE, PHONE_TEL, EMAIL, EMAIL_MAILTO } from "../seo/routes";
import { homeFaqs } from "../seo/content";
import { Seo } from "../components/Seo";
import { SectionHead, FAQBlock, CTABand, LinkGrid } from "../components/Blocks";
import { IconTile, SERVICE_ICONS, SECTOR_ICONS, INSIGHT_ICON } from "../components/Icons";

const stats = [
  { value: "13", label: "Provinces & Territories" },
  { value: "690+", label: "Cities & Communities" },
  { value: "20", label: "Specialized Services" },
  { value: "24/7", label: "Dispatch & Response" },
];

const why = [
  { icon: ShieldCheck, title: "Licensed in Every Province", text: "Every guard holds a valid provincial or territorial licence — PSISA in Ontario, BSP in Quebec, Security Services Act in BC and beyond — verified before their first shift." },
  { icon: Radar, title: "Verified Patrols & Reporting", text: "GPS-stamped checkpoints, supervisor audits and time-stamped digital incident reports give you documented proof of coverage after every shift." },
  { icon: Radio, title: "24/7 Live Dispatch", text: "One phone line, answered around the clock. Urgent coverage — fire watch, alarm response, strike security — coordinated the moment you call." },
  { icon: FileCheck2, title: "One Accountable Partner", text: "Site walk-throughs, written post orders and a single command structure from coast to coast. No subcontractor roulette, no excuses." },
];

const head = getHead("/", { faqs: homeFaqs });

export default function Home() {
  return (
    <>
      <Seo title={head.title} description={head.description} path="/" />
      <section className="relative overflow-hidden border-b border-white/10" data-testid="home-hero">
        <div className="absolute inset-0">
          <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "linear-gradient(rgba(255,255,255,.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.5) 1px, transparent 1px)", backgroundSize: "56px 56px" }} />
          <Shield className="absolute -right-16 top-1/2 -translate-y-1/2 h-[420px] w-[420px] text-[#FF5500] opacity-[0.07]" strokeWidth={0.5} />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-36">
          <div className="inline-flex items-center gap-2 border border-[#FF5500]/40 bg-[#FF5500]/10 px-3 py-1.5 mb-6 animate-fade-up" data-testid="hero-dispatch-badge">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
            </span>
            <span className="text-[11px] font-mono uppercase tracking-[0.25em] text-[#FF8A50]">24/7 Dispatch Active · Coast to Coast</span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight uppercase leading-none text-white max-w-4xl animate-fade-up" style={{ animationDelay: "80ms" }}>
            Licensed Security Guard Services <span className="text-[#FF5500]">Across Canada</span>
          </h1>
          <p className="mt-6 text-lg text-gray-300 leading-relaxed max-w-2xl animate-fade-up" style={{ animationDelay: "160ms" }}>
            Rapid deployment of certified, uniformed security guards for commercial, industrial, construction and high-risk environments — in every province, territory and major Canadian city.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 animate-fade-up" style={{ animationDelay: "240ms" }}>
            <a href={PHONE_TEL} data-testid="hero-phone-button" className="inline-flex items-center justify-center gap-2 bg-[#FF5500] hover:bg-[#E04800] text-white font-semibold px-7 py-3.5 rounded-sm transition-colors duration-200">
              <Phone className="h-4 w-4" /> Call Dispatch: {PHONE}
            </a>
            <a href={EMAIL_MAILTO} data-testid="hero-email-button" className="inline-flex items-center justify-center gap-2 bg-white/5 hover:bg-white/10 border border-white/15 text-white font-medium px-7 py-3.5 rounded-sm transition-colors duration-200">
              <Mail className="h-4 w-4 text-[#FF5500]" /> Email: {EMAIL}
            </a>
          </div>
        </div>
      </section>

      <section className="border-b border-white/10 bg-[#131620]" data-testid="stats-strip">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-2 md:grid-cols-4 gap-6">
          {stats.map((s) => (
            <div key={s.label} className="text-center md:text-left" data-testid={`stat-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <p className="font-display text-4xl font-extrabold text-[#FF5500]">{s.value}</p>
              <p className="text-xs font-mono uppercase tracking-[0.2em] text-gray-400 mt-1">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24" data-testid="home-services">
        <SectionHead
          overline="What We Do"
          title="20 Specialized Security Services"
          sub="From uniformed static guards to remote CCTV monitoring, every service is delivered by provincially licensed personnel with verified, documented coverage."
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((s) => (
            <Link
              key={s.slug}
              to={`/services/${s.slug}`}
              data-testid={`service-card-${s.slug}`}
              className="group border border-white/10 bg-[#181C28] rounded-sm overflow-hidden transition-all duration-200 hover:-translate-y-1 hover:border-[#FF5500]/50 hover:shadow-[0_10px_30px_rgba(255,85,0,0.15)]"
            >
              <IconTile icon={SERVICE_ICONS[s.slug] || Shield} className="h-36 border-b border-white/10" />
              <div className="p-5">
                <h3 className="font-display text-lg font-bold uppercase tracking-tight text-white group-hover:text-[#FF5500] transition-colors">{s.name}</h3>
                <p className="mt-2 text-sm text-gray-400 leading-relaxed">{s.short}</p>
                <span className="mt-3 inline-flex items-center gap-1 text-xs font-mono uppercase tracking-wider text-[#FF5500]">
                  Details <ArrowRight className="h-3 w-3" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="bg-[#131620] border-y border-white/10" data-testid="why-maze">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <SectionHead
            overline="Why Maze"
            title="Security You Can Verify"
            sub="Any company can promise protection. Maze Security Solutions proves it — with licensing, supervision and documentation on every engagement."
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {why.map((w) => (
              <div key={w.title} className="border border-white/10 bg-[#0D0E12] p-6 rounded-sm" data-testid={`why-${w.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <w.icon className="h-8 w-8 text-[#FF5500] mb-4" />
                <h3 className="font-display text-xl font-bold uppercase tracking-tight text-white">{w.title}</h3>
                <p className="mt-2 text-sm text-gray-400 leading-relaxed">{w.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24" data-testid="home-sectors">
        <SectionHead
          overline="Who We Protect"
          title="Security by Industry Sector"
          sub="Specialized guard programs designed for the risks, regulations and rhythms of your industry."
        />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {sectors.map((s) => (
            <Link
              key={s.slug}
              to={`/sectors/${s.slug}`}
              data-testid={`sector-card-${s.slug}`}
              className="group relative border border-white/10 rounded-sm overflow-hidden h-56 flex items-end transition-all duration-200 hover:-translate-y-1 hover:border-[#FF5500]/50"
            >
              <IconTile icon={SECTOR_ICONS[s.slug] || Shield} size="h-16 w-16" className="absolute inset-0 h-full" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0D0E12] via-[#0D0E12]/60 to-transparent" />
              <div className="relative p-5">
                <h3 className="font-display text-xl font-bold uppercase tracking-tight text-white">{s.name}</h3>
                <p className="mt-1 text-xs text-gray-400">{s.blurb}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <LinkGrid
        overline="Where We Operate"
        title="Security Guard Services by Province & Territory"
        items={provinces.map((p) => ({ to: `/security-guards-${p.slug}`, label: p.name, testid: `province-link-${p.slug}` }))}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6 pb-4">
        <Link to="/service-areas" data-testid="view-all-cities-link" className="inline-flex items-center gap-1.5 text-sm text-[#FF5500] hover:text-white transition-colors font-medium">
          Browse all 690+ Canadian cities <ArrowRight className="h-4 w-4" />
        </Link>
      </div>

      <section className="bg-[#131620] border-y border-white/10" data-testid="home-insights">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <SectionHead overline="Insights" title="From the Security Briefing Room" sub="Practical guidance on licensing, loss prevention, fire watch and site protection in Canada." />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {insights.slice(0, 3).map((a) => (
              <Link key={a.slug} to={`/insights/${a.slug}`} data-testid={`insight-card-${a.slug}`} className="group border border-white/10 bg-[#0D0E12] rounded-sm overflow-hidden transition-all duration-200 hover:-translate-y-1 hover:border-[#FF5500]/50">
                <IconTile icon={INSIGHT_ICON} size="h-9 w-9" className="h-32 border-b border-white/10" />
                <div className="p-5">
                  <h3 className="font-display text-lg font-bold tracking-tight text-white group-hover:text-[#FF5500] transition-colors">{a.title}</h3>
                  <p className="mt-2 text-sm text-gray-400 leading-relaxed">{a.excerpt}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <FAQBlock faqs={homeFaqs} />
      <CTABand title="Need Guards on Site?" sub="One call reaches our 24/7 dispatch. Tell us the property, the risk and the timeline — we handle the rest." />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <p className="text-xs text-gray-500 leading-relaxed max-w-4xl">
          Maze Security Solutions provides licensed security guard services, mobile security patrol, construction site security, event security, concierge security, retail loss prevention, fire watch guards, alarm response and CCTV monitoring across Canada — including {cities.slice(0, 14).map((c) => c.name).join(", ")} and dozens of additional communities nationwide.
        </p>
      </div>
    </>
  );
}
